import React from "react";
import styled from "styled-components";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { ButtonList } from "@sonder/ui/source/styled/fragments/ButtonList";
import { RadioDropdownItem } from "@sonder/ui/source/styled/fragments/RadioDropdownItem";
import { DropdownContentAlignment } from "@sonder/ui/source/styled/surfaces/DropdownContent";
import { OptionType, ValueType } from "@sonder/ui/source/styled/types";

const StyledDropdownContent = styled(DropdownContent)`
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  > svg {
    margin-left: 0.7rem;
  }
`;

const StyledButtonList = styled(ButtonList)`
  max-height: 16.5rem;
  min-width: 12rem;
  padding-bottom: 0.5rem;
  max-width: 22rem;
  width: 100%;
`;

interface RenderTriggerProps {
  className?: string;
  disabled?: boolean;
  label?: string;
  options: OptionType[];
  selected?: ValueType;
}

interface RadioSelectorProps extends RenderTriggerProps {
  alignContent?: DropdownContentAlignment;
  renderTrigger?: (args: RenderTriggerProps) => JSX.Element;
  required?: boolean;
  onChange: (value: ValueType) => void;
}

const RadioSelectorTrigger = ({
  className,
  disabled,
  label = "Select...",
  options,
  selected,
}: RenderTriggerProps) => {
  const selectedOption =
    selected && options.filter(option => option.value === selected)[0];
  return (
    <StyledButton className={className} disabled={disabled}>
      {selectedOption ? selectedOption.displayName : label}
      <ChevronDown />
    </StyledButton>
  );
};

export const RadioSelector = ({
  alignContent,
  className,
  disabled,
  label,
  onChange,
  options,
  renderTrigger = RadioSelectorTrigger,
  required,
  selected,
}: RadioSelectorProps) => {
  const handleChange = (value: ValueType) =>
    onChange(selected === value && !required ? null : value);

  return (
    <Dropdown
      trigger={renderTrigger({
        className,
        disabled,
        label,
        options,
        selected,
      })}
    >
      {({ closePortal, isOpen: isPortalOpen }) => {
        const optionButtons = options.map(option => {
          const isSelected = option.value === selected;
          return (
            <RadioDropdownItem
              checked={isSelected}
              key={option.displayName}
              onClick={() => {
                closePortal();
                handleChange(option.value);
              }}
            >
              {option.displayName}
            </RadioDropdownItem>
          );
        });
        return (
          isPortalOpen && (
            <StyledDropdownContent align={alignContent}>
              <StyledButtonList>{optionButtons}</StyledButtonList>
            </StyledDropdownContent>
          )
        );
      }}
    </Dropdown>
  );
};
